/* ==================================================
  商品カード画像スライダー
===================================================*/

/*  一覧ページでは商品カードが数十枚並ぶため、カードごとに初期化せず
    document への委譲リスナーだけで処理する。
    横方向の操作はタッチ（Touch Events）・マウス（Pointer Events）とも JS でスクロールさせ、
    画像上で始めた横スワイプは外側の商品一覧（横スクロールリスト・Splide）に伝えない。
    縦スクロールはブラウザに任せる。
    Boost の商品カードはカード要素自体に遷移用のクリックハンドラが付いているため、
    ドラッグ直後のクリックは capture フェーズで受けて stopPropagation し、遷移を止める。  */

(() => {
  const SLIDER_SELECTOR = '[data-product-card-slider]';
  const VIEWPORT_SELECTOR = '[data-product-card-slider-viewport]';
  const PROGRESS_SELECTOR = '[data-product-card-slider-progress]';
  const DRAG_START_THRESHOLD = 5;
  const SLIDE_CHANGE_RATIO = 0.15;
  const SNAP_RESTORE_DELAY = 600;

  /* Boost が後から描画する一覧の先頭画像は初期 HTML からは発見できない。
     DOM に追加された直後に画面上部の2枚だけ通信優先度を上げ、LCP 候補の取得を早める。 */
  function promoteInitialBoostImages() {
    const images = Array.from(document.querySelectorAll('img.boost-sd__product-image-img--main'))
      .filter((image) => image.getBoundingClientRect().top < window.innerHeight * 1.5)
      .slice(0, 2);

    images.forEach((image) => {
      image.loading = 'eager';
      image.fetchPriority = 'high';
    });

    return images.length >= 2;
  }

  if (!promoteInitialBoostImages() && 'MutationObserver' in window && document.body) {
    const boostImageObserver = new MutationObserver(() => {
      if (promoteInitialBoostImages()) boostImageObserver.disconnect();
    });
    boostImageObserver.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => boostImageObserver.disconnect(), 10000);
  }

  /* Boost のページネーションは表示範囲外の番号に aria-hidden を付けるが、
     button 自体はフォーカス可能なままになる。非表示中だけ disabled を同期し、
     Boost が表示対象へ戻したときはテーマが付けた属性だけ解除する。 */
  function syncBoostPaginationAccessibility() {
    document.querySelectorAll('button.boost-sd__pagination-number').forEach((button) => {
      if (button.getAttribute('aria-hidden') === 'true') {
        button.disabled = true;
        button.tabIndex = -1;
        button.dataset.tabioA11yDisabled = 'true';
      } else if (button.dataset.tabioA11yDisabled === 'true') {
        button.disabled = false;
        button.removeAttribute('tabindex');
        delete button.dataset.tabioA11yDisabled;
      }
    });
  }

  syncBoostPaginationAccessibility();
  if ('MutationObserver' in window && document.body) {
    let paginationA11yRaf = 0;
    new MutationObserver(() => {
      if (paginationA11yRaf) return;
      paginationA11yRaf = window.requestAnimationFrame(() => {
        paginationA11yRaf = 0;
        syncBoostPaginationAccessibility();
      });
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
  }

  /*  一覧では2枚目以降、トップでは1枚目も表示直前までURLを渡さず、画面外カードの通信を抑える。 */
  function hydrateImage(image) {
    if (!(image instanceof HTMLImageElement) || !image.hasAttribute('data-product-card-deferred-image')) {
      return;
    }

    if (image.dataset.srcset) {
      image.srcset = image.dataset.srcset;
    }

    if (image.dataset.src) {
      image.src = image.dataset.src;
    }

    image.removeAttribute('data-src');
    image.removeAttribute('data-srcset');
    image.removeAttribute('data-product-card-deferred-image');
  }

  function hydrateAround(viewport, index = 0) {
    [index, index + 1].forEach((slideIndex) => {
      const slide = viewport.children[slideIndex];
      const image = slide && slide.querySelector('[data-product-card-deferred-image]');
      hydrateImage(image);
    });
  }

  if ('IntersectionObserver' in window) {
    const viewportObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        hydrateAround(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '500px 0px' });

    document.querySelectorAll(VIEWPORT_SELECTOR).forEach((viewport) => viewportObserver.observe(viewport));
  } else {
    document.querySelectorAll('[data-product-card-deferred-image]').forEach(hydrateImage);
  }

  /*  RTLロケールでは scrollLeft が負値になるため符号を持たせる  */
  const direction = document.documentElement.getAttribute('dir') === 'rtl' ? -1 : 1;

  function getScrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /* ------------------------------
    プログレスバー
  ------------------------------*/

  /*  CFCL と同じく左から伸びる表示。スクロール量に合わせて連続的に伸縮させる  */
  function updateProgress(viewport) {
    const slider = viewport.closest(SLIDER_SELECTOR);
    const progress = slider && slider.querySelector(PROGRESS_SELECTOR);
    const slideWidth = viewport.clientWidth;
    const total = viewport.children.length;

    if (!progress || !slideWidth || total < 2) {
      return;
    }

    const position = Math.min(Math.max(Math.abs(viewport.scrollLeft) / slideWidth, 0), total - 1);
    hydrateAround(viewport, Math.round(position));
    progress.style.setProperty('--c-card-slider-position', String(position + 1));
  }

  /* Boost は検索・絞り込みごとにカードを再生成する。旧テンプレートでもバーを補完する。 */
  function syncProgress(slider) {
    const viewport = slider.querySelector(VIEWPORT_SELECTOR);
    if (!viewport) {
      return;
    }

    const total = viewport.children.length;
    let progress = slider.querySelector(PROGRESS_SELECTOR);
    if (total > 1 && !progress) {
      const track = document.createElement('div');
      track.className = 'c_product-card-slider__progress';
      track.setAttribute('aria-hidden', 'true');
      progress = document.createElement('span');
      progress.className = 'c_product-card-slider__progress-fill';
      progress.setAttribute('data-product-card-slider-progress', '');
      track.appendChild(progress);
      slider.appendChild(track);
    }

    if (progress) {
      progress.style.setProperty('--c-card-slider-total', String(Math.max(total, 1)));
      progress.parentElement.hidden = total < 2;
      updateProgress(viewport);
    }
    slider.classList.toggle('c_product-card-slider--has-progress', total > 1);
  }

  function initProgress() {
    document.querySelectorAll(SLIDER_SELECTOR).forEach(syncProgress);
    const observer = new MutationObserver((records) => {
      const sliders = new Set();
      records.forEach((record) => {
        if (record.target instanceof Element) {
          const slider = record.target.closest(SLIDER_SELECTOR);
          if (slider) {
            sliders.add(slider);
          }
        }
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }
          if (node.matches(SLIDER_SELECTOR)) {
            sliders.add(node);
          }
          node.querySelectorAll(SLIDER_SELECTOR).forEach((slider) => sliders.add(slider));
        });
      });
      sliders.forEach((slider) => {
        if (slider.isConnected) {
          syncProgress(slider);
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProgress, { once: true });
  } else {
    initProgress();
  }

  const pendingViewports = new Set();

  /*  scroll はバブリングしないため capture で受け、描画1回につき1度だけ更新する  */
  document.addEventListener('scroll', (e) => {
    const viewport = e.target;

    if (!(viewport instanceof Element) || !viewport.matches(VIEWPORT_SELECTOR)) {
      return;
    }

    if (pendingViewports.size === 0) {
      window.requestAnimationFrame(() => {
        pendingViewports.forEach(updateProgress);
        pendingViewports.clear();
      });
    }

    pendingViewports.add(viewport);
  }, { capture: true, passive: true });

  /* ------------------------------
    スワイプ・ドラッグ共通
  ------------------------------*/

  let drag = null;

  function startDrag(viewport, x, y) {
    hydrateAround(viewport, Math.round(Math.abs(viewport.scrollLeft) / viewport.clientWidth));

    drag = {
      viewport: viewport,
      startX: x,
      startY: y,
      lastX: x,
      startScrollLeft: viewport.scrollLeft,
      startIndex: Math.round(Math.abs(viewport.scrollLeft) / viewport.clientWidth),
      moved: false,
    };
  }

  /*  指・マウスの移動量ぶん画像をスクロールさせる。初回の移動でスナップを外す  */
  function moveDrag(x) {
    const { viewport } = drag;
    const dx = x - drag.startX;
    drag.lastX = x;

    if (!drag.moved) {
      drag.moved = true;
      window.clearTimeout(viewport._cardSliderSnapTimer);
      viewport.classList.add('is-dragging');
      viewport.dataset.cardSliderDragged = 'true';
    }

    viewport.scrollLeft = drag.startScrollLeft - dx;
  }

  function finishDrag(x) {
    const { viewport, startX, startIndex, moved } = drag;
    drag = null;

    if (!moved) {
      return;
    }

    const slideWidth = viewport.clientWidth;
    const total = viewport.children.length;
    const dx = x - startX;
    let targetIndex = startIndex;

    /*  一定量以上ドラッグしたら、ドラッグ方向へ1枚だけ送る（ループはしない）  */
    if (Math.abs(dx) > slideWidth * SLIDE_CHANGE_RATIO) {
      targetIndex += dx * direction < 0 ? 1 : -1;
    }

    targetIndex = Math.min(Math.max(targetIndex, 0), total - 1);
    hydrateAround(viewport, targetIndex);

    viewport.scrollTo({ left: direction * slideWidth * targetIndex, behavior: getScrollBehavior() });

    /*  スナップを戻すのはスクロール完了後。途中で戻すと位置が飛ぶ  */
    window.clearTimeout(viewport._cardSliderSnapTimer);
    viewport._cardSliderSnapTimer = window.setTimeout(() => {
      viewport.classList.remove('is-dragging');
    }, SNAP_RESTORE_DELAY);
  }

  function findViewport(target) {
    if (!(target instanceof Element)) {
      return null;
    }

    const viewport = target.closest(VIEWPORT_SELECTOR);

    if (!viewport || viewport.children.length < 2 || !viewport.clientWidth) {
      return null;
    }

    return viewport;
  }

  document.addEventListener('mouseover', (e) => {
    const viewport = findViewport(e.target);

    if (viewport) {
      hydrateAround(viewport, Math.round(Math.abs(viewport.scrollLeft) / viewport.clientWidth));
    }
  }, { passive: true });

  /* ------------------------------
    タッチ（スワイプ）
  ------------------------------*/

  /*  iOS Safari は touch-action: pan-y を指定してもスクロール要素のネイティブスクロールが走り、
      Pointer Events 側の処理と打ち消し合って前の画像へ戻れないことがあるため、
      タッチは Touch Events で受け、横方向と判定したら touchmove を preventDefault して
      ネイティブスクロール（画像・外側の一覧とも）を止めたうえで JS で動かす。
      Splide は track 要素に capture で touchstart を付けてドラッグを始めるため、
      それより先に届く window の capture で stopPropagation し、外側スライダーに操作を渡さない。  */
  let touchAxis = null;

  window.addEventListener('touchstart', (e) => {
    const viewport = findViewport(e.target);

    if (!viewport) {
      return;
    }

    e.stopPropagation();

    if (e.touches.length !== 1) {
      drag = null;
      return;
    }

    touchAxis = null;
    delete viewport.dataset.cardSliderDragged;
    startDrag(viewport, e.touches[0].clientX, e.touches[0].clientY);
  }, { capture: true, passive: true });

  window.addEventListener('touchmove', (e) => {
    if (!drag || e.touches.length !== 1) {
      return;
    }

    const touch = e.touches[0];
    const dx = touch.clientX - drag.startX;
    const dy = touch.clientY - drag.startY;

    if (touchAxis === null) {
      if (Math.abs(dx) < DRAG_START_THRESHOLD && Math.abs(dy) < DRAG_START_THRESHOLD) {
        return;
      }

      touchAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }

    /*  縦方向はページスクロールとしてブラウザに任せる  */
    if (touchAxis !== 'x') {
      drag = null;
      return;
    }

    if (e.cancelable) {
      e.preventDefault();
    }

    moveDrag(touch.clientX);
  }, { capture: true, passive: false });

  function onTouchEnd(e) {
    if (!drag) {
      return;
    }

    const touch = e.changedTouches[0];
    finishDrag(touch ? touch.clientX : drag.lastX);
  }

  window.addEventListener('touchend', onTouchEnd, true);
  window.addEventListener('touchcancel', onTouchEnd, true);

  /* ------------------------------
    PC のマウスドラッグ
  ------------------------------*/

  /*  Splide は mousedown でもドラッグを始めるため、タッチと同様に window の capture で止める  */
  window.addEventListener('mousedown', (e) => {
    if (findViewport(e.target)) {
      e.stopPropagation();
    }
  }, true);

  let mousePointerId = null;

  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) {
      return;
    }

    const viewport = findViewport(e.target);

    if (!viewport) {
      return;
    }

    delete viewport.dataset.cardSliderDragged;
    mousePointerId = e.pointerId;
    startDrag(viewport, e.clientX, e.clientY);
  });

  document.addEventListener('pointermove', (e) => {
    if (!drag || e.pointerId !== mousePointerId) {
      return;
    }

    if (!drag.moved) {
      if (Math.abs(e.clientX - drag.startX) < DRAG_START_THRESHOLD) {
        return;
      }

      /*  キャプチャはドラッグ確定後に取る。押した時点で取ると通常クリックがリンクに届かない  */
      drag.viewport.setPointerCapture(e.pointerId);
    }

    moveDrag(e.clientX);
  });

  function onMouseUp(e) {
    if (!drag || e.pointerId !== mousePointerId) {
      return;
    }

    if (drag.viewport.hasPointerCapture(e.pointerId)) {
      drag.viewport.releasePointerCapture(e.pointerId);
    }

    mousePointerId = null;

    /*  pointercancel の座標は 0 になることがあり、左へ大きくドラッグした扱いで
        常に「次へ」送られてしまうため、最後に受け取った座標で判定する  */
    finishDrag(e.type === 'pointercancel' ? drag.lastX : e.clientX);
  }

  document.addEventListener('pointerup', onMouseUp);
  document.addEventListener('pointercancel', onMouseUp);

  /*  ドラッグ直後のクリックでは商品ページへ遷移させない  */
  document.addEventListener('click', (e) => {
    const viewport = e.target.closest(VIEWPORT_SELECTOR);

    if (!viewport || viewport.dataset.cardSliderDragged !== 'true') {
      return;
    }

    delete viewport.dataset.cardSliderDragged;
    e.preventDefault();
    e.stopPropagation();
  }, true);

  /*  画像・リンクのネイティブドラッグ（ゴースト画像）を抑止。
      Boost のカードは viewport の外側を商品リンクが包んでおり、dragstart が viewport 外の
      リンクで発生してブラウザがポインター操作を中断（pointercancel）するため、
      スライダー操作中とスライダーを含むリンクでも止める  */
  document.addEventListener('dragstart', (e) => {
    if (!(e.target instanceof Element)) {
      return;
    }

    if (drag || e.target.closest(SLIDER_SELECTOR) || e.target.closest(VIEWPORT_SELECTOR) || e.target.querySelector(VIEWPORT_SELECTOR)) {
      e.preventDefault();
    }
  });
})();
